import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SnackbarProvider, useSnackbar } from "notistack";
import { payEmployeeSalary } from "../store/slices/salarySlice";
import { fetchAllUsers } from "../store/slices/authSlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import SalarySlipModal from "./SalarySlipModal";
import {
  FileText,
  DollarSign,
  ChevronDown,
  User,
  Building,
} from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const SalaryHistory = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.auth);
  const { activeSociety, theme, gradient } = useSidebarContext();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedEmployeeForSlip, setSelectedEmployeeForSlip] = useState(null);
  const [paidStatus, setPaidStatus] = useState({});

  const years = Array.from(
    { length: new Date().getFullYear() - 2023 + 1 },
    (_, i) => 2023 + i
  );

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  useEffect(() => {
    const savedPaidStatus =
      JSON.parse(localStorage.getItem("paidStatus")) || {};
    setPaidStatus(savedPaidStatus);
  }, []);

  useEffect(() => {
    localStorage.setItem("paidStatus", JSON.stringify(paidStatus));
  }, [paidStatus]);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  const societyEmployees = React.useMemo(() => {
    if (!activeSociety || !users) return [];
    return users.filter((user) =>
      activeSociety.users.some(
        (societyUser) =>
          societyUser._id === user._id &&
          user.employeeData &&
          user.role === "Employee"
      )
    );
  }, [activeSociety, users]);

  const usersNeedingSalaryPayment = societyEmployees.filter((user) => {
    if (!user.employeeData || !user.employeeData.salaryDetails) return false;

    const { dateOfJoining } = user.employeeData.personalDetails;
    const joiningDate = new Date(dateOfJoining);
    if (
      joiningDate.getFullYear() > selectedYear ||
      (joiningDate.getFullYear() === selectedYear &&
        joiningDate.getMonth() + 1 > selectedMonth)
    ) {
      return false;
    }

    return true;
  });

  const handlePaySalary = async (employeeId, employeeDetails) => {
    const paidKey = `${employeeId}-${selectedYear}-${selectedMonth}`;

    if (paidStatus[paidKey]) {
      enqueueSnackbar(
        `Salary already paid for ${employeeDetails.username} in ${selectedYear}-${selectedMonth}`,
        {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        }
      );
      return;
    }

    const result = await MySwal.fire({
      title: "Pay Salary Confirmation",
      html: `
        <div class="${theme.tailwind.text9}">
          <p><strong>Society:</strong> ${activeSociety.name}</p>
          <p><strong>Username:</strong> ${employeeDetails.username}</p>
          <p><strong>Designation:</strong> ${employeeDetails.designation}</p>
          <p><strong>Salary:</strong> PKR${employeeDetails.salaryDetails.salary.toLocaleString()}</p>
          <p>Do you want to proceed with salary payment?</p>
        </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay Salary",
      cancelButtonText: "Cancel",
      background: `${theme.mui.BgLinear}`,
      customClass: {
        title: `${theme.tailwind.text9}`,
        confirmButton: `${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white w-26 h-12 p-2 rounded-lg`,
        cancelButton: `bg-gray-600 hover:bg-gray-700 text-white ml-3 w-26 h-12 p-2 rounded-lg`,
      },
      buttonsStyling: false,
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(payEmployeeSalary(employeeId));

        if (response.error) {
          MySwal.fire({
            title: "Payment Already Made",
            html: `
              <div class="${theme.tailwind.text9}">
                <p><strong>Error:</strong> Payment could not be processed.</p>
                <p>Please check the details and try again.</p>
              </div>`,
            icon: "error",
            showCancelButton: false,
            confirmButtonText: "Close",
            background: `${theme.mui.BgLinear}`,
            customClass: {
              title: `${theme.tailwind.text9}`,
              confirmButton: `${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white w-26 h-12 p-2 rounded-lg`,
            },
            buttonsStyling: false,
          });
        } else {
          setPaidStatus((prevStatus) => ({
            ...prevStatus,
            [paidKey]: true,
          }));

          enqueueSnackbar(
            `Salary paid successfully for ${employeeDetails.username}`,
            {
              variant: "success",
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
            }
          );
        }
      } catch (error) {
        console.error("Error paying salary: ", error);
        await MySwal.fire({
          title: "Payment Error",
          html: `
            <div class="${theme.tailwind.text9}">
              <p><strong>Error:</strong> Payment could not be processed.</p>
              <p>Please check the details and try again.</p>
            </div>`,
          icon: "error",
          showCancelButton: false,
          confirmButtonText: "Close",
          background: `${theme.mui.BgLinear}`,
          customClass: {
            title: `${theme.tailwind.text9}`,
            confirmButton: `${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white w-26 h-12 p-2 rounded-lg`,
          },
          buttonsStyling: false,
        });
      }
    }
  };

  const handleGenerateSalarySlip = (user) => {
    if (user && user.employeeData) {
      setSelectedEmployeeForSlip(user);
    } else {
      console.error("Invalid user data for salary slip", user);
      enqueueSnackbar(
        "Unable to generate salary slip. Invalid employee data.",
        {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        }
      );
    }
  };

  if (!activeSociety) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className={`${theme.tailwind.text9} text-xl`}>
          Please select a society to view salary history.
        </div>
      </div>
    );
  }

  return (
    <div className={`${gradient} min-h-screen p-6 rounded-xl shadow-lg`}>
      <div
        className={`${theme.tailwind.bg6} backdrop-blur-sm rounded-2xl shadow-2xl border ${theme.tailwind.border1} h-full`}
      >
        {/* Header */}
        <div className={`p-6 border-b ${theme.tailwind.border1}`}>
          <div
            className={`relative overflow-hidden rounded-lg p-6 ${gradient} shadow-xl`}
            style={{
              backgroundImage: `
        linear-gradient(to bottom right, ${theme.mui.bg2}),
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
      `,
            }}
          >
            <div className="absolute inset-0 opacity-10">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="10"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                      className={`${theme.tailwind.text9}`}
                    />
                  </pattern>
                  <pattern
                    id="circles"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="2"
                      fill="currentColor"
                      className={`${theme.tailwind.text9}`}
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)" />
                <rect width="100" height="100" fill="url(#circles)" />
              </svg>
            </div>

            <div className="relative">
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-3 group">
                  <div
                    className={`p-2 rounded-lg ${theme.tailwind.bg11} transform group-hover:scale-110 transition-transform duration-300`}
                  >
                    <DollarSign className={`w-8 h-8 ${theme.tailwind.text9}`} />
                  </div>
                  <h2
                    className={`text-3xl font-bold ${theme.tailwind.text9} tracking-tight`}
                  >
                    Salary Management
                  </h2>
                </div>

                <div
                  className={`flex items-center space-x-2 ml-14 ${theme.tailwind.text4} ${theme.tailwind.hover1} transition-colors duration-300`}
                >
                  <Building className="w-5 h-5 m" />
                  <span className="text-lg">{activeSociety.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 flex space-x-4">
          {/* Year Dropdown */}
          <div className="flex-1">
            <label className={`block ${theme.tailwind.text9} mb-2`}>Year</label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 appearance-none`}
              >
                {years.map((year) => (
                  <option
                    key={year}
                    value={year}
                    className={`${theme.tailwind.bg14}`}
                  >
                    {year}
                  </option>
                ))}
              </select>
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${theme.tailwind.text9}`}
              >
                <ChevronDown className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Month Dropdown */}
          <div className="flex-1">
            <label className={`block ${theme.tailwind.text9} mb-2`}>
              Month
            </label>
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 appearance-none`}
              >
                {months.map((month) => (
                  <option
                    key={month.value}
                    value={month.value}
                    className={`${theme.tailwind.bg14}`}
                  >
                    {month.label}
                  </option>
                ))}
              </select>
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${theme.tailwind.text9}`}
              >
                <ChevronDown className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="p-6">
          <div className="overflow-x-auto">
            <table
              className={`w-full border-collapse ${theme.tailwind.bg14} rounded-lg overflow-hidden`}
            >
              <thead>
                <tr className={`${theme.tailwind.bg5} ${theme.tailwind.text9}`}>
                  <th className="p-4 text-left">Employee</th>
                  <th className="p-4 text-left">Designation</th>
                  <th className="p-4 text-left">Basic Salary</th>
                  <th className="p-4 text-left">Allowances</th>
                  <th className="p-4 text-left">Bounes</th>
                  <th className="p-4 text-left">Deductions</th>
                  <th className="p-4 text-left">Net Salary</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersNeedingSalaryPayment.length > 0 ? (
                  usersNeedingSalaryPayment.map((user) => {
                    const salaryDetails = user.employeeData.salaryDetails || {};
                    const netSalary =
                      (salaryDetails.salary || 0) +
                      (salaryDetails.bonuses || 0) +
                      (salaryDetails.allowances || 0) -
                      (salaryDetails.deductions || 0);

                    const paidKey = `${user.employeeData._id}-${selectedYear}-${selectedMonth}`;
                    const isPaid = paidStatus[paidKey];

                    return (
                      <tr
                        key={user._id}
                        className={`border-b ${theme.tailwind.border19} ${theme.tailwind.bgHover15} transition-all duration-300`}
                      >
                        <td
                          className={`p-4 ${theme.tailwind.text9} flex items-center`}
                        >
                          <User className="mr-2 w-5 h-5" />
                          {user.username}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9}`}>
                          {user.employeeData.designation}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9}`}>
                          PKR {(salaryDetails.salary || 0).toLocaleString()}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9}`}>
                          PKR {(salaryDetails.allowances || 0).toLocaleString()}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9}`}>
                          PKR {(salaryDetails.bonuses || 0).toLocaleString()}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9}`}>
                          PKR {(salaryDetails.deductions || 0).toLocaleString()}
                        </td>
                        <td className={`p-4 ${theme.tailwind.text9} font-bold`}>
                          PKR {netSalary.toLocaleString()}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex space-x-2 justify-center">
                            <button
                              onClick={() => {
                                const paidKey = `${user.employeeData._id}-${selectedYear}-${selectedMonth}`;
                                if (paidStatus[paidKey]) {
                                  MySwal.fire({
                                    icon: "error",
                                    title: "Payment Error",
                                    text: `Salary for ${user.username} has already been paid for ${selectedMonth}-${selectedYear}.`,
                                  });
                                } else {
                                  handlePaySalary(user.employeeData._id, {
                                    username: user.username,
                                    designation: user.employeeData.designation,
                                    salaryDetails:
                                      user.employeeData.salaryDetails,
                                  });
                                }
                              }}
                              className={`px-3 py-2 rounded-lg flex items-center ${
                                paidStatus[
                                  `${user.employeeData._id}-${selectedYear}-${selectedMonth}`
                                ]
                                  ? `bg-gray-500 cursor-not-allowed opacity-50`
                                  : `${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white`
                              }`}
                              disabled={
                                paidStatus[
                                  `${user.employeeData._id}-${selectedYear}-${selectedMonth}`
                                ]
                              }
                            >
                              {paidStatus[
                                `${user.employeeData._id}-${selectedYear}-${selectedMonth}`
                              ] ? (
                                "Paid"
                              ) : (
                                <>
                                  <DollarSign className="mr-1 w-4 h-4" />
                                  Pay
                                </>
                              )}
                            </button>

                            <button
                              onClick={() => handleGenerateSalarySlip(user)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center"
                            >
                              <FileText className="mr-1 w-4 h-4" />
                              Slip
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className={`p-4 text-center ${theme.tailwind.text9}`}
                    >
                      No records found for the selected month and year.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {selectedEmployeeForSlip && (
          <SalarySlipModal
            employeeDetails={selectedEmployeeForSlip}
            salaryDetails={selectedEmployeeForSlip.employeeData.salaryDetails}
            onClose={() => setSelectedEmployeeForSlip(null)}
          />
        )}
      </div>
    </div>
  );
};

const SalaryHistoryWrapper = () => {
  return (
    <SnackbarProvider maxSnack={3}>
      <SalaryHistory />
    </SnackbarProvider>
  );
};

export default SalaryHistoryWrapper;
